import { useCallback, useRef, useState } from "react";
import {
  fetchPartnershipPartnerOptions,
  type PartnershipPartnerOptionsLoad,
} from "../utils/fetchPartnershipPartnerOptions";
import {
  partnershipPartnerOptionsFetchKey,
  type PartnershipPartnerOptionsFetchParams,
  type RefetchPartnershipPartnerOptions,
} from "../utils/partnershipPartnerOptionsFetch";

type ApplyLoad = (
  catalog: Awaited<ReturnType<typeof fetchPartnershipPartnerOptions>>
) => void;

export type { RefetchPartnershipPartnerOptions };

export type PartnershipPartnerOptionsCache = {
  refetch: RefetchPartnershipPartnerOptions;
  catalogByKey: Readonly<Record<string, PartnershipPartnerOptionsLoad>>;
  getCatalogForParams: (
    args: PartnershipPartnerOptionsFetchParams
  ) => PartnershipPartnerOptionsLoad | undefined;
};

/**
 * Wraps partnership_partner_options loads: per-advertiser-identity cache, in-flight
 * dedupe per key, stale-response guard, and optional applyLoad for parent screens.
 */
export function useDedupedPartnershipPartnerOptionsRefetch(
  applyLoad: ApplyLoad,
  setLoading: (loading: boolean) => void,
  setError: (message: string | null) => void
): PartnershipPartnerOptionsCache {
  const [catalogByKey, setCatalogByKey] = useState<
    Record<string, PartnershipPartnerOptionsLoad>
  >({});
  const lastCompletedKeysRef = useRef<Set<string>>(new Set());
  const generationByKeyRef = useRef<Map<string, number>>(new Map());
  const inFlightByKeyRef = useRef<Map<string, Promise<void>>>(new Map());
  const inFlightCountRef = useRef(0);

  const beginLoading = useCallback(() => {
    inFlightCountRef.current += 1;
    if (inFlightCountRef.current === 1) {
      setLoading(true);
    }
  }, [setLoading]);

  const endLoading = useCallback(() => {
    inFlightCountRef.current = Math.max(0, inFlightCountRef.current - 1);
    if (inFlightCountRef.current === 0) {
      setLoading(false);
    }
  }, [setLoading]);

  const refetch = useCallback<RefetchPartnershipPartnerOptions>(
    async (args, options) => {
      const key = partnershipPartnerOptionsFetchKey(args);
      if (!options?.force && lastCompletedKeysRef.current.has(key)) {
        return;
      }

      const existingInFlight = inFlightByKeyRef.current.get(key);
      if (!options?.force && existingInFlight) {
        await existingInFlight;
        return;
      }

      const generation = (generationByKeyRef.current.get(key) ?? 0) + 1;
      generationByKeyRef.current.set(key, generation);

      beginLoading();
      let promise!: Promise<void>;
      promise = (async () => {
        try {
          const mapped = await fetchPartnershipPartnerOptions(args);
          if (generationByKeyRef.current.get(key) !== generation) {
            return;
          }
          setCatalogByKey((prev) => ({ ...prev, [key]: mapped }));
          lastCompletedKeysRef.current.add(key);
          applyLoad(mapped);
          setError(null);
        } catch (e) {
          if (generationByKeyRef.current.get(key) !== generation) {
            return;
          }
          setError(
            e instanceof Error
              ? e.message
              : "Could not load pages and Instagram accounts"
          );
        } finally {
          endLoading();
          if (inFlightByKeyRef.current.get(key) === promise) {
            inFlightByKeyRef.current.delete(key);
          }
        }
      })();

      inFlightByKeyRef.current.set(key, promise);
      await promise;
    },
    [applyLoad, beginLoading, endLoading, setError]
  );

  const getCatalogForParams = useCallback(
    (args: PartnershipPartnerOptionsFetchParams) =>
      catalogByKey[partnershipPartnerOptionsFetchKey(args)],
    [catalogByKey]
  );

  return { refetch, catalogByKey, getCatalogForParams };
}
